import { defineStore } from 'pinia';
import { useDepartmentStore } from '@/stores/department';
import { cloneDeep } from 'lodash';
import { format } from '@/utils/date-fns';
import { useProfilesStore } from '@/stores/profiles';
import { getUniqueInstitutions } from '@/utils/helpers';
import { IInstitution } from '@/interfaces';
import api from '@/api';
import { MealEntry } from '@/graphql/custom-types';

export interface IMealState {
  all: MealEntry[];
  selected: MealEntry;
  uniqueInstitutions: IInstitution[];
}

const DEFAULT_MEAL: MealEntry = {
  __typename: 'SingleMeal',
  id: '',
  title: '',
  startDate: '',
  endDate: '',
  coverImage: {
    __typename: 'Picture',
    id: '',
    source: '',
    blurhash: '',
  },
  participants: [],
  signupPossible: false,
  signupEnd: '',
  cancelled: false,
  pictures: [],
  institution: {
    __typename: 'Institution',
    id: '',
    name: '',
    color: '',
    logo: {
      __typename: 'Picture',
      id: '',
      source: '',
    },
  },
  departments: [],
  dishes: [],
  description: '',
  maxSignups: null,
  signupStart: null,
  documents: null,
  responsible: null,
};

export const useMealsStore = defineStore('meals', {
  state: (): IMealState & { allCopy: MealEntry[] } => ({
    all: [],
    selected: DEFAULT_MEAL,
    uniqueInstitutions: [],
    allCopy: [],
  }),
  actions: {
    async getMeals() {
      const departmentStore: any = useDepartmentStore();

      if (!departmentStore.id) {
        throw new Error('No department id provided');
      }

      const dateNow = new Date();
      const daysForward = departmentStore.settings?.meal?.daysForward || 90;

      const dateStart = dateNow.setDate(dateNow.getDate() + daysForward - 1); // subtract one as we should include today's date
      const dateEnd = new Date(dateStart);

      api.meal
        .getMealRange(departmentStore.id, dateEnd)
        .then((mealRange) => {
          this.all = [...mealRange];
          this.allCopy = [...mealRange];
          this.uniqueInstitutions = getUniqueInstitutions(mealRange);
        })
        .catch((error) => {
          console.log('Error getting meals', error);
        });
    },
    async getPcdMeals() {
      const departmentStore = useDepartmentStore();

      if (!departmentStore.id) {
        throw new Error('No department id provided');
      }

      await api.meal
        .getPcdMeals(format(new Date(), 'yyyy-MM-dd'))
        .then((pcdMeals) => {
          const meals = pcdMeals.reduce((acc: any, meal: any) => {
            const courses = meal.courses.map((course: any) => {
              return {
                isPcd: true,
                id: course.id,
                title: course.title,
                category: course.category,
                startDate: meal.date,
                coverImage: {
                  source: '/img/external/external-meal.jpg',
                },
              };
            });

            acc.push(...courses);
            return acc;
          }, []);

          this.all = meals;
        })
        .catch((error) => {
          console.log('Error getting meals', error);
        });
    },
    async getSelectedMeal(mealId: string) {
      await api.meal
        .getMealById(mealId)
        .then((mealEntry) => {
          this.selected = mealEntry;
        })
        .catch((error) => {
          console.log('Could not get meal entry: ', error);
        });
    },
    async mealSignupScreen({ subscription, profile, participating }: any) {
      const profilesStore = useProfilesStore();
      const mutate = participating
        ? api.meal.mealSignup
        : api.meal.mealCancelSignup;

      await mutate(subscription, profile)
        .then(async () => {
          profilesStore.setPinValidation({
            validating: false,
            validPin: true,
          });

          this.getSelectedMeal(subscription.id);
        })
        .catch((error) => {
          profilesStore.setPinValidation({
            validating: false,
            validPin: false,
          });

          throw new Error('Error signing up to meal', error);
        });
    },
    async updateParticipants(mealId: string) {
      await api.meal
        .getMealById(mealId)
        .then((mealEntry) => {
          const participants = mealEntry.participants;

          const allMeals = cloneDeep(this.all);
          const mealIndex = allMeals.findIndex((meal) => meal.id === mealId);

          if (mealIndex > -1) {
            allMeals[mealIndex].participants = participants;
            this.all = allMeals;
          }
        })
        .catch((error) => {
          console.log('Error: ', error);
        });
    },
  },
});
